/**
 * useOrejaSessions - Multi-session manager for Oreja.
 *
 * Tracks which sessions exist, their metadata, and provides badge/status info.
 * Individual `useOrejaSession` hooks are instantiated by panel components per tab,
 * not within this hook (to avoid dynamic hook calls).
 *
 * @module useOrejaSessions
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { queryKeys } from '@/lib/query-keys';
import {
  MAX_CONCURRENT_SESSIONS,
  ATTENTION_PHASES,
  PROCESSING_PHASES,
  type OrejaPhase,
  type OrejaSession,
} from '@/lib/oreja/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Metadata for a tracked session (lightweight, no hook state). */
export interface OrejaSessionEntry {
  sessionId: string;
  clientId: string;
  projectId: string;
  clickupListId: string;
  phase: OrejaPhase;
  createdAt: string;
}

/** Return value of useOrejaSessions. */
export interface UseOrejaSessionsReturn {
  /** Active session entries (metadata only). */
  sessions: Map<string, OrejaSessionEntry>;
  /** Currently focused session ID. */
  activeSessionId: string | null;

  /** Number of sessions needing user attention (for badge). */
  badgeCount: number;
  /** Whether any session is currently processing. */
  isProcessing: boolean;

  /** Creates a new session entry. Returns the session ID. */
  startSession: (params: {
    clientId: string;
    projectId: string;
    clickupListId: string;
  }) => string;
  /** Switches focus to a different session. */
  switchSession: (sessionId: string) => void;
  /** Removes a session from the active set. */
  closeSession: (sessionId: string) => void;
  /** Updates the phase for a tracked session (called by panel components). */
  updateSessionPhase: (sessionId: string, phase: OrejaPhase) => void;

  /** Past session history from the database. */
  history: OrejaSession[];
  /** Whether history is loading. */
  isLoadingHistory: boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Multi-session manager for Oreja. Tracks session metadata, badge counts,
 * and provides history query.
 *
 * @returns Session management state and actions.
 */
export function useOrejaSessions(): UseOrejaSessionsReturn {
  const { user } = useAuth();
  const { organization } = useOrganization();

  const orgId = organization?.id ?? '';
  const userId = user?.id ?? '';

  // Active sessions map
  const [sessions, setSessions] = useState<Map<string, OrejaSessionEntry>>(new Map());
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const badgeCount = useMemo(() => {
    let count = 0;
    for (const entry of sessions.values()) {
      if (ATTENTION_PHASES.includes(entry.phase)) {
        count++;
      }
    }
    return count;
  }, [sessions]);

  const isProcessing = useMemo(() => {
    for (const entry of sessions.values()) {
      if (PROCESSING_PHASES.includes(entry.phase)) {
        return true;
      }
    }
    return false;
  }, [sessions]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Creates a new session entry and returns its ID.
   * Throws if max concurrent sessions would be exceeded.
   *
   * @param params - Client, project, and ClickUp list info.
   * @returns The new session ID.
   */
  const startSession = useCallback((params: {
    clientId: string;
    projectId: string;
    clickupListId: string;
  }): string => {
    const nonCommittedCount = Array.from(sessions.values()).filter(
      s => s.phase !== 'committed'
    ).length;

    if (nonCommittedCount >= MAX_CONCURRENT_SESSIONS) {
      throw new Error(
        `Maximum ${MAX_CONCURRENT_SESSIONS} concurrent sessions allowed. Close an existing session first.`
      );
    }

    const sessionId = crypto.randomUUID();
    const entry: OrejaSessionEntry = {
      sessionId,
      clientId: params.clientId,
      projectId: params.projectId,
      clickupListId: params.clickupListId,
      phase: 'recording',
      createdAt: new Date().toISOString(),
    };

    setSessions(prev => {
      const next = new Map(prev);
      next.set(sessionId, entry);
      return next;
    });
    setActiveSessionId(sessionId);

    return sessionId;
  }, [sessions]);

  /**
   * Switches focus to a different session.
   *
   * @param sessionId - The session to focus.
   */
  const switchSession = useCallback((sessionId: string) => {
    if (sessions.has(sessionId)) {
      setActiveSessionId(sessionId);
    }
  }, [sessions]);

  /**
   * Removes a session from the active set. Switches focus if the closed
   * session was the active one.
   *
   * @param sessionId - The session to close.
   */
  const closeSession = useCallback((sessionId: string) => {
    setSessions(prev => {
      const next = new Map(prev);
      next.delete(sessionId);
      return next;
    });

    if (activeSessionId === sessionId) {
      // Switch to first remaining session, or null
      setSessions(prev => {
        const remaining = Array.from(prev.keys());
        setActiveSessionId(remaining.length > 0 ? remaining[0] : null);
        return prev;
      });
    }
  }, [activeSessionId]);

  /**
   * Updates the phase of a tracked session. Called by panel components
   * when their useOrejaSession hook changes phase.
   *
   * @param sessionId - The session to update.
   * @param phase - The new phase.
   */
  const updateSessionPhase = useCallback((sessionId: string, phase: OrejaPhase) => {
    setSessions(prev => {
      const entry = prev.get(sessionId);
      if (!entry || entry.phase === phase) return prev;

      const next = new Map(prev);
      next.set(sessionId, { ...entry, phase });
      return next;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // History query
  // ---------------------------------------------------------------------------

  const { data: history = [], isLoading: isLoadingHistory } = useQuery({
    queryKey: queryKeys.oreja.history(orgId),
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('oreja_sessions')
        .select('id, organization_id, user_id, client_id, project_id, clickup_list_id, phase, audio_storage_path, transcript, questions, answers, proposals, refinement_inputs, committed_changes, input_mode, model_used, created_at, updated_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('[Oreja] Failed to fetch session history:', error.message);
        return [];
      }

      return (data ?? []) as unknown as OrejaSession[];
    },
    enabled: !!orgId && !!userId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    sessions,
    activeSessionId,
    badgeCount,
    isProcessing,
    startSession,
    switchSession,
    closeSession,
    updateSessionPhase,
    history,
    isLoadingHistory,
  };
}
